import { zodResolver } from '@hookform/resolvers/zod';
import { type EDifficultyType, ELightGalleryType, type EOsceType } from '@quesmed/types/models';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FaBoxOpen } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { AssetModal, RecordAssetEditor } from '@/components/assets';
import { getAiConfigFormDefaults } from '@/components/station/ai-config-form';
import { DEFAULT_PHASE_TIMERS, parsePhaseTimersFromApi } from '@/components/station/phase-timers-utils';
import { type IStationSchema, stationSchema } from '@/components/station/schema';
import type { IAssetSchema } from '@/config';
import { EFormMode } from '@/config/globals';
import { useLazyEffect } from '@/core/hooks';
import { Form, Skeleton } from '@/core/ui';
import { AdminStationDocument } from '@/graphql/gql/graphql';
import { NetworkStatus, useQuery } from '@/libs';
import { useAsset, useGlobal, useRecord, useStation } from '@/store';
import { Left } from './components/left';
import { Right } from './components/right';
import { Top } from './components/top';

export function StationEditPage() {
  const dbType = useGlobal.use.dbType();
  const setAssetMapping = useAsset.use.setAssetMapping();
  const setOriginalAssets = useAsset.use.setOriginalAssets();
  const setStation = useStation.use.setStation();
  const setFormMode = useRecord.use.setFormMode();
  const { id } = useParams<{ id: string }>();
  const isEmpty = !id || id === 'null';

  const { data, loading, error, refetch, networkStatus } = useQuery(AdminStationDocument, {
    variables: { id: Number(id) },
    skip: isEmpty || !dbType,
    fetchPolicy: 'network-only',
  });
  const station = data?.restricted?.osceStation;

  const form = useForm<IStationSchema>({
    mode: 'onChange',
    resolver: zodResolver(stationSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      marks: [],
      phaseTimers: DEFAULT_PHASE_TIMERS,
    },
  });

  useEffect(() => {
    setFormMode(EFormMode.EDIT);
    if (station && station?.id?.toString() === id) {
      setStation(station);
      const timeoutId = setTimeout(() => {
        const assets: IAssetSchema[] =
          ([...(station.pictures ?? [])]?.map((asset) => ({
            id: asset?.id?.toString(),
            index: asset?.index,
            jobId: '',
            choiceLabel: undefined,
            recordId: '',
            deleted: false,
            createdAt: asset?.createdAt,
            linked: true,
            type: asset?.typeId ?? ELightGalleryType.EXPLANATION,
            picture: asset,
          })) as unknown as IAssetSchema[]) ?? [];
        setAssetMapping(assets);
        setOriginalAssets(assets);
        form.reset({
          assets,
          marks: station?.marks
            ? Object.values(
                station.marks.reduce((acc: any, m, idx) => {
                  const header = m?.header as string;
                  if (!acc[header]) {
                    acc[header] = { header, choices: [], index: idx };
                  }
                  acc[header].choices.push({
                    index: m?.index ?? idx,
                    name: m?.name ?? '',
                    weight: m?.weight ?? 1,
                    excludeFromAi: m?.excludeFromAi || false,
                  });
                  return acc;
                }, {}),
              )
            : [],
          aiEnabled: station?.aiEnabled ?? false,
          aiConfig: getAiConfigFormDefaults(station?.aiConfig),
          picture: station?.picture ? (station.picture as IStationSchema['picture']) : undefined,
          enabled: String(station?.enabled ?? 0),
          explanation: station?.explanation || '',
          examinerQuestions: (station?.examinerQuestions || []).map((item, index) => ({
            index: item?.index ?? index + 1,
            question: item?.question ?? '',
          })),
          actorBrief: station?.actorBrief || '',
          llmActorBrief: station?.llmActorBrief || '',
          examinerBrief: station?.examinerBrief || '',
          candidateBrief: station?.candidateBrief || '',
          name: station?.name || '',
          entitlementIds: station?.entitlements?.map((e) => e?.id?.toString()) ?? [],
          hiddenName: station?.hiddenName || '',
          difficulty: station?.difficulty as EDifficultyType,
          osceTypeId: station?.osceTypeId as EOsceType,
          topicIds: station?.topics?.map((t) => t?.id?.toString()) ?? [],
          phaseTimers: parsePhaseTimersFromApi(station?.phaseTimers),
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [station, networkStatus]);

  useLazyEffect(() => {
    if (!isEmpty && dbType && networkStatus === NetworkStatus.ready) {
      refetch();
    }
  }, [dbType]);

  return (
    <Form {...form}>
      <form>
        <div className='flex flex-col h-full rounded-sm border border-default-200'>
          {/* Header Section */}
          <header className='bg-background flex items-center shadow-sm border-b border-default-200'>
            <Top disabled={false} loading={loading} refetch={refetch} />
          </header>
          {/* Main Content */}
          <main className='flex bg-background divide-x divide-default-200 max-h-[calc(100dvh-210px)] h-full flex-1 flex-row'>
            {isEmpty ? (
              <div className='text-center w-full flex flex-col py-44 items-center justify-center gap-y-4'>
                <FaBoxOpen className='size-12' />
                <p className='text-default-500'>Enter Station ID to edit</p>
              </div>
            ) : loading ? (
              <div className='w-full p-6 grid grid-cols-12 h-[40rem] gap-6'>
                <Skeleton className='col-span-7 rounded-md shadow-sm' />
                <Skeleton className='col-span-5 rounded-md shadow-sm' />
              </div>
            ) : station ? (
              <>
                <section className='basis-7/12 p-4 scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100 overflow-auto'>
                  <Left />
                </section>
                <aside className='basis-5/12 p-4 scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100 overflow-auto'>
                  <Right />
                </aside>
              </>
            ) : (
              <div className='text-center w-full flex flex-col py-20 items-center justify-center gap-y-4'>
                <FaBoxOpen className='size-12' />
                <p className='text-default-500'>{error?.message || 'Station not found'}</p>
              </div>
            )}
          </main>
        </div>
      </form>
      <AssetModal type='normal' />
      <RecordAssetEditor type='normal' />
    </Form>
  );
}
